import React from "react";
import { View } from "@tarojs/components";
import classNames from "classnames";
import useFlexGapSupport from "@/hooks/useFlexGapSupport";
import { toArray } from "@/utils";
import { ConfigContext, SizeType } from "./config-provider";
import Item from "./Item";
import type { XHComponentCommonProps } from "../../types";

import "./index.scss";

export const SpaceConfigContext = ConfigContext;
export type SpaceProps = {
  direction?: "horizontal" | "vertical";
  align?: "start" | "end" | "center" | "baseline";
  size?: SizeType | [SizeType, SizeType];
  style?: React.CSSProperties;
  split?: React.ReactNode;
  wrap?: boolean;
} & XHComponentCommonProps;

export const SpaceContext = React.createContext({
  latestIndex: 0,
  horizontalSize: 0,
  verticalSize: 0,
  supportFlexGap: false,
});

const spaceSize = {
  small: 8,
  normal: 16,
  large: 24,
};

function getNumberSize(size: SizeType) {
  return typeof size === "string" ? spaceSize[size] : size || 0;
}

const XHSpace: React.FC<SpaceProps> = props => {
  const {
    getPrefixCls,
    space,
    direction: directionConfig,
  } = React.useContext(ConfigContext);

  const {
    size = space?.size || "small",
    align,
    className,
    children,
    direction = "horizontal",
    split,
    style,
    wrap = false,
    ...otherProps
  } = props;

  const supportFlexGap = useFlexGapSupport();

  const [horizontalSize, verticalSize] = React.useMemo(
    () =>
      ((Array.isArray(size) ? size : [size, size]) as [SizeType, SizeType]).map(
        item => getNumberSize(item)
      ),
    [size]
  );

  const childNodes = toArray(children, { keepEmpty: true });

  const mergedAlign =
    align === undefined && direction === "horizontal" ? "center" : align;
  const prefixCls = getPrefixCls("space");
  const cn = classNames(
    prefixCls,
    `${prefixCls}-${direction}`,
    {
      [`${prefixCls}-rtl`]: directionConfig === "rtl",
      [`${prefixCls}-align-${mergedAlign}`]: mergedAlign,
    },
    className
  );

  const itemClassName = `${prefixCls}-item`;

  const marginDirection =
    directionConfig === "rtl" ? "marginLeft" : "marginRight";

  // Calculate latest one
  let latestIndex = 0;
  const nodes = childNodes.map((child: any, i) => {
    if (child !== null && child !== undefined) {
      latestIndex = i;
    }

    const key = (child && child.key) || `${itemClassName}-${i}`;

    return (
      <Item
        className={itemClassName}
        key={key}
        direction={direction}
        index={i}
        marginDirection={marginDirection}
        split={split}
        wrap={wrap}
      >
        {child}
      </Item>
    );
  });

  const spaceContext = React.useMemo(
    () => ({ horizontalSize, verticalSize, latestIndex, supportFlexGap }),
    [horizontalSize, verticalSize, latestIndex, supportFlexGap]
  );

  if (childNodes.length === 0) {
    return null;
  }

  const gapStyle: React.CSSProperties = {};

  if (wrap) {
    gapStyle.flexWrap = "wrap";

    if (!supportFlexGap) {
      gapStyle.marginBottom = `-${verticalSize}px`;
    }
  }

  if (supportFlexGap) {
    gapStyle.columnGap = `${horizontalSize}px`;
    gapStyle.rowGap = `${verticalSize}px`;
  }

  return (
    <View
      className={cn}
      style={{
        ...gapStyle,
        ...style,
      }}
      {...otherProps}
    >
      <SpaceContext.Provider value={spaceContext}>
        {nodes}
      </SpaceContext.Provider>
    </View>
  );
};

export default XHSpace;
